import { useEffect, useMemo, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { useApi, useMutation } from '../../lib/useApi.js';
import { api } from '../../lib/apiClient.js';
import { usePageMetadata } from '../../app/PageMetaContext.jsx';
import { useCurrentUser } from '../../app/CurrentUserContext.jsx';
import { TimeEntryDrawer } from '../time/TimeEntryDrawer.jsx';
import {
  Breadcrumbs,
  ErrorState,
  PageActionStrip,
  PageHeader,
  StatusChip,
  KpiHeroBand,
  KpiBandSkeleton,
  ChartSkeleton,
  BrandedEmptyState,
  StatusDonutMini,
  WorkflowPipeline,
  buildPipelineSteps,
  MiniBars,
  deriveKpiTone,
  toast,
  activityRail,
} from '../../components/index.js';
import './delivery.css';
import './delivery.redesign.css';

function upsertEnvelopeRow(prev, record) {
  if (!Array.isArray(prev?.data)) return prev;
  const exists = prev.data.some((item) => item.id === record.id);
  const nextRows = exists
    ? prev.data.map((item) => (item.id === record.id ? { ...item, ...record } : item))
    : [...prev.data, record];
  return {
    ...prev,
    data: nextRows,
    page: prev.page ? { ...prev.page, total: nextRows.length } : prev.page,
  };
}

function removeEnvelopeRow(prev, recordId) {
  if (!Array.isArray(prev?.data)) return prev;
  const nextRows = prev.data.filter((item) => item.id !== recordId);
  return {
    ...prev,
    data: nextRows,
    page: prev.page ? { ...prev.page, total: nextRows.length } : prev.page,
  };
}

function normalizeWorkItemStatus(targetBucket) {
  const statusMap = {
    todo: 'open',
    in_progress: 'in_progress',
    blocked: 'blocked',
    review: 'in_review',
    done: 'done',
  };
  return statusMap[targetBucket] || targetBucket;
}

// Canonical execution lifecycle for a single work item. The API and the demo
// seed mix camelCase (`inProgress`), snake_case (`in_progress`), and friendly
// labels (`review`), so collapse everything onto these five canonical keys.
const WORK_ITEM_LIFECYCLE = ['open', 'in_progress', 'in_review', 'done'];
const WORK_ITEM_LIFECYCLE_LABELS = {
  open: 'Open',
  in_progress: 'In progress',
  in_review: 'In review',
  done: 'Done',
};

function canonicalWorkItemStatus(status) {
  const raw = String(status || 'open')
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, '_');
  const map = {
    todo: 'open',
    backlog: 'open',
    open: 'open',
    new: 'open',
    inprogress: 'in_progress',
    in_progress: 'in_progress',
    active: 'in_progress',
    doing: 'in_progress',
    blocked: 'blocked',
    impeded: 'blocked',
    review: 'in_review',
    inreview: 'in_review',
    in_review: 'in_review',
    done: 'done',
    complete: 'done',
    completed: 'done',
    closed: 'done',
  };
  return map[raw] || raw;
}

// Map a canonical status onto an executive-traffic-light tone name.
function statusTone(canonical) {
  switch (canonical) {
    case 'done':
      return 'sage';
    case 'in_review':
      return 'blue';
    case 'in_progress':
      return 'blue';
    case 'blocked':
      return 'brick';
    default:
      return 'slate';
  }
}

const STATUS_DISTRIBUTION_ORDER = ['open', 'in_progress', 'in_review', 'blocked', 'done'];
const STATUS_DISTRIBUTION_LABELS = {
  open: 'Open',
  in_progress: 'In progress',
  in_review: 'In review',
  blocked: 'Blocked',
  done: 'Done',
};

export function WorkItemDetailPage() {
  const { id = '' } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const returnTo = searchParams.get('returnTo') || '';
  // ?logTime=1 is the canonical deep link that replaced the retired
  // /work-items/:id/log-time route (LEGACY_ROUTE_ALIASES): it opens the
  // governed time-entry drawer directly on the work-item record.
  const timeDrawerOpen = searchParams.get('logTime') === '1';
  const logTimeSearch = (() => {
    const params = new URLSearchParams(searchParams);
    params.set('logTime', '1');
    return `?${params.toString()}`;
  })();
  function closeTimeDrawer() {
    const params = new URLSearchParams(searchParams);
    params.delete('logTime');
    setSearchParams(params, { replace: true });
  }
  const workItemRes = useApi(id ? `/work-items/${id}` : null, { skip: !id });
  const workItem = workItemRes.data?.data || null;
  const projectId = workItem?.projectId || '';
  const checklist = useApi(id ? `/work-items/${id}/checklist` : null, {
    skip: !id,
    deps: [id],
  });
  const dependencyLinks = useApi(id ? `/work-items/${id}/dependencies` : null, {
    skip: !id,
    deps: [id],
  });
  const siblingWorkItems = useApi('/work-items', {
    query: projectId ? { projectId, limit: 200 } : undefined,
    skip: !projectId,
    deps: [projectId],
  });
  const deliverySummary = useApi(projectId ? `/projects/${projectId}/delivery-summary` : null, {
    skip: !projectId,
    deps: [projectId],
  });
  const update = useMutation();
  const checklistMutation = useMutation();
  const dependencyMutation = useMutation();
  const { user } = useCurrentUser();
  const [newChecklistLabel, setNewChecklistLabel] = useState('');
  const [newChecklistDueDate, setNewChecklistDueDate] = useState('');
  const [newDependencyPredecessorId, setNewDependencyPredecessorId] = useState('');
  const [newDependencyLagDays, setNewDependencyLagDays] = useState('0');

  usePageMetadata({
    title: workItem?.title ? `Work item · ${workItem.title}` : 'Work item detail',
    subtitle: 'Checklist, dependencies, schedule posture, and governed delivery actions.',
  });

  const allWorkItems = useMemo(() => siblingWorkItems.data?.data || [], [siblingWorkItems.data]);
  const workItemById = useMemo(() => {
    return new Map(allWorkItems.map((item) => [item.id, item]));
  }, [allWorkItems]);
  const checklistItems = checklist.data?.data || [];
  const dependencyRows = useMemo(() => {
    return (dependencyLinks.data?.data || []).map((dependency) => {
      const isPredecessor = dependency.successorId === id;
      const relatedId = isPredecessor ? dependency.predecessorId : dependency.successorId;
      const relatedItem = workItemById.get(relatedId);
      return {
        ...dependency,
        relationLabel: isPredecessor ? 'Blocked by' : 'Blocks',
        relatedId,
        relatedTitle: relatedItem?.title || relatedId || 'Unmapped work item',
      };
    });
  }, [dependencyLinks.data, id, workItemById]);
  const availablePredecessors = useMemo(() => {
    if (!id) return [];
    const existingPredecessorIds = new Set(
      dependencyRows
        .filter((dependency) => dependency.successorId === id)
        .map((dependency) => dependency.predecessorId),
    );
    return allWorkItems.filter((item) => item.id !== id && !existingPredecessorIds.has(item.id));
  }, [allWorkItems, dependencyRows, id]);

  useEffect(() => {
    setNewChecklistLabel('');
    setNewChecklistDueDate('');
    setNewDependencyPredecessorId('');
    setNewDependencyLagDays('0');
  }, [id]);

  async function moveWorkItem(targetBucket) {
    if (!workItem) return;
    try {
      const response = await update.run(() =>
        api.patch(`/work-items/${workItem.id}`, {
          ...workItem,
          status: normalizeWorkItemStatus(targetBucket),
        }),
      );
      const updated = response.data || response;
      workItemRes.setData?.((prev) => ({
        ...(prev || {}),
        data: {
          ...(prev?.data || {}),
          ...updated,
        },
      }));
      siblingWorkItems.setData?.((prev) => {
        if (!Array.isArray(prev?.data)) return prev;
        return {
          ...prev,
          data: prev.data.map((item) => (item.id === workItem.id ? { ...item, ...updated } : item)),
        };
      });
      deliverySummary.refresh?.();
      toast.success({
        title: 'Work item updated',
        body: `${workItem.title} → ${targetBucket.replace('_', ' ')}`,
      });
      activityRail.push({
        action: 'work_item.update',
        targetType: 'work_item',
        targetId: workItem.id,
        actorUserId: user?.id || 'unknown',
        summary: `${workItem.title} → ${targetBucket}`,
      });
    } catch (error) {
      toast.error({ title: 'Could not update work item', body: error.message });
    }
  }

  async function addChecklistItem() {
    if (!id || !newChecklistLabel.trim()) return;
    try {
      const payload = {
        label: newChecklistLabel.trim(),
        dueDate: newChecklistDueDate || null,
      };
      const response = await checklistMutation.run(() =>
        api.post(`/work-items/${id}/checklist`, payload),
      );
      const created = response.data || response;
      checklist.setData?.((prev) => upsertEnvelopeRow(prev, created));
      setNewChecklistLabel('');
      setNewChecklistDueDate('');
      deliverySummary.refresh?.();
      toast.success({
        title: 'Checklist item created',
        body: created.label,
      });
      activityRail.push({
        action: 'work_item.checklist.create',
        targetType: 'work_item',
        targetId: id,
        actorUserId: user?.id || 'unknown',
        summary: `Added checklist item ${created.label?.slice(0, 64)}`,
      });
    } catch (error) {
      toast.error({ title: 'Could not add checklist item', body: error.message });
    }
  }

  async function updateChecklistItem(item, status) {
    if (!id || !item) return;
    try {
      const response = await checklistMutation.run(() =>
        api.patch(`/work-items/${id}/checklist/${item.id}`, {
          ...item,
          status,
        }),
      );
      const updated = response.data || response;
      checklist.setData?.((prev) => upsertEnvelopeRow(prev, updated));
      deliverySummary.refresh?.();
      toast.success({
        title: 'Checklist updated',
        body: `${item.label} → ${status.replace('_', ' ')}`,
      });
      activityRail.push({
        action: 'work_item.checklist.update',
        targetType: 'work_item',
        targetId: id,
        actorUserId: user?.id || 'unknown',
        summary: `${item.label?.slice(0, 64)} → ${status}`,
      });
    } catch (error) {
      toast.error({ title: 'Could not update checklist item', body: error.message });
    }
  }

  async function addDependencyLink() {
    if (!id || !newDependencyPredecessorId) return;
    try {
      const payload = {
        predecessorId: newDependencyPredecessorId,
        dependencyType: 'finish_to_start',
        lagDays: Number(newDependencyLagDays || 0),
      };
      const response = await dependencyMutation.run(() =>
        api.post(`/work-items/${id}/dependencies`, payload),
      );
      const created = response.data || response;
      dependencyLinks.setData?.((prev) => upsertEnvelopeRow(prev, created));
      setNewDependencyPredecessorId('');
      setNewDependencyLagDays('0');
      deliverySummary.refresh?.();
      toast.success({
        title: 'Dependency linked',
        body: `${created.predecessorId} now blocks ${id}`,
      });
      activityRail.push({
        action: 'work_item.dependency.create',
        targetType: 'work_item',
        targetId: id,
        actorUserId: user?.id || 'unknown',
        summary: `Linked predecessor ${created.predecessorId}`,
      });
    } catch (error) {
      toast.error({ title: 'Could not link dependency', body: error.message });
    }
  }

  async function removeDependencyLink(dependency) {
    if (!id || !dependency) return;
    try {
      await dependencyMutation.run(() =>
        api.delete(`/work-items/${id}/dependencies/${dependency.id}`),
      );
      dependencyLinks.setData?.((prev) => removeEnvelopeRow(prev, dependency.id));
      deliverySummary.refresh?.();
      toast.success({
        title: 'Dependency removed',
        body: `${dependency.relatedId || dependency.predecessorId} no longer blocks this work item.`,
      });
      activityRail.push({
        action: 'work_item.dependency.delete',
        targetType: 'work_item',
        targetId: id,
        actorUserId: user?.id || 'unknown',
        summary: `Removed dependency ${dependency.id}`,
      });
    } catch (error) {
      toast.error({ title: 'Could not remove dependency', body: error.message });
    }
  }

  if (workItemRes.loading) {
    return (
      <section className="delivery-redesign delivery-detail-loading" aria-busy="true">
        <KpiBandSkeleton count={4} />
        <ChartSkeleton height={120} />
      </section>
    );
  }
  if (workItemRes.error) return <ErrorState error={workItemRes.error} />;
  if (!workItem) {
    return (
      <section className="delivery-redesign">
        <BrandedEmptyState
          icon="🛰️"
          eyebrow="OWHA Delivery"
          title="Work item not found"
          description="The requested delivery record is unavailable or outside your current scope."
          action={
            <Link className="btn secondary sm" to="/delivery">
              Back to delivery workspace
            </Link>
          }
          tone="slate"
        />
      </section>
    );
  }

  const defaultDeliveryRoute = projectId ? `/projects/${projectId}?tab=delivery` : '/delivery';
  const roadmapRoute = projectId ? `/projects/${projectId}?tab=roadmap` : '/roadmap';
  const deliveryLink = returnTo || defaultDeliveryRoute;
  const deliverySummaryData = deliverySummary.data?.data || null;

  // BUG FIX: the tiles previously read milestoneSlipDays / criticalPathCount,
  // which the /projects/:id/delivery-summary endpoint never returns — so two of
  // three tiles always rendered "—". The real fields are milestoneSlipCount,
  // criticalPathItemCount, and baselineVarianceDays (see buildDeliverySummary in
  // apps/api/src/services/deliveryPlanning.service.js). Wire the real fields and
  // fall back to derived/legacy values only where present.
  const blockedWorkValue = deliverySummaryData?.blockedWorkCount ?? null;
  const milestoneSlipValue =
    deliverySummaryData?.milestoneSlipCount ?? deliverySummaryData?.milestoneSlipDays ?? null;
  const criticalPathValue =
    deliverySummaryData?.criticalPathItemCount ??
    deliverySummaryData?.criticalPathCount ??
    (dependencyRows.length ? dependencyRows.filter((item) => item.criticalPathFlag).length : null);
  const baselineVarianceValue = deliverySummaryData?.baselineVarianceDays ?? null;

  // ---- Derived engagement views (reuse already-fetched data only) ---------
  const currentStatus = canonicalWorkItemStatus(workItem.status);
  const isBlocked = currentStatus === 'blocked';
  // For the chevron ribbon, blocked items render against their *flow* lifecycle
  // (open→in_progress→in_review→done) with the in-progress step forced to a
  // blocked tone so the impediment reads at a glance.
  const pipelineSteps = buildPipelineSteps({
    order: WORK_ITEM_LIFECYCLE,
    current: isBlocked ? 'in_progress' : currentStatus,
    labels: WORK_ITEM_LIFECYCLE_LABELS,
    statusByKey: isBlocked ? { in_progress: 'blocked' } : {},
  });

  // Project-wide status distribution donut: prefer the server statusBuckets,
  // fall back to counting the already-fetched sibling work items. Omitted when
  // neither source has data.
  const distributionCounts = (() => {
    const buckets = deliverySummaryData?.statusBuckets;
    const acc = {};
    if (buckets && typeof buckets === 'object') {
      for (const [key, count] of Object.entries(buckets)) {
        const canonical = canonicalWorkItemStatus(key);
        acc[canonical] = (acc[canonical] || 0) + (Number(count) || 0);
      }
    } else {
      for (const item of allWorkItems) {
        const canonical = canonicalWorkItemStatus(item.status);
        acc[canonical] = (acc[canonical] || 0) + 1;
      }
    }
    return acc;
  })();
  const distributionSegments = STATUS_DISTRIBUTION_ORDER.map((key) => ({
    label: STATUS_DISTRIBUTION_LABELS[key],
    count: distributionCounts[key] || 0,
    tone: statusTone(key),
  })).filter((segment) => segment.count > 0);
  const distributionTotal = distributionSegments.reduce((sum, s) => sum + s.count, 0);

  // Checklist completion microviz — done vs. remaining from the already-fetched
  // checklist envelope.
  const checklistTotal = checklistItems.length;
  const checklistDone = checklistItems.filter(
    (item) => canonicalWorkItemStatus(item.status) === 'done',
  ).length;
  const checklistRemaining = Math.max(0, checklistTotal - checklistDone);
  const checklistPct = checklistTotal ? Math.round((checklistDone / checklistTotal) * 100) : null;

  // Completion KPI: prefer the explicit percentComplete; tone it on a target of
  // 100% with a small "near" band.
  const completionPct =
    typeof workItem.percentComplete === 'number' ? workItem.percentComplete : null;
  const completionTone =
    completionPct == null
      ? 'slate'
      : completionPct >= 100
        ? 'sage'
        : completionPct >= 60
          ? 'blue'
          : completionPct >= 30
            ? 'amber'
            : 'brick';

  const heroItems = [
    {
      id: 'completion',
      surface: 'work-item-hero',
      label: 'Completion',
      value: completionPct ?? '—',
      unit: completionPct != null ? '%' : undefined,
      status: completionTone,
      footer:
        completionPct != null
          ? completionPct >= 100
            ? 'Work item fully delivered'
            : `${100 - completionPct}% remaining to close`
          : 'No completion telemetry recorded',
    },
    {
      id: 'blocked-work',
      surface: 'work-item-hero',
      label: 'Blocked work',
      value: blockedWorkValue ?? '—',
      unit:
        typeof blockedWorkValue === 'number'
          ? blockedWorkValue === 1
            ? 'item'
            : 'items'
          : undefined,
      status: deriveKpiTone({
        value: typeof blockedWorkValue === 'number' ? blockedWorkValue : 0,
        target: 0,
        invert: true,
        atRisk: typeof blockedWorkValue === 'number' && blockedWorkValue > 0,
      }),
      footer: 'Items blocked across this project',
    },
    {
      id: 'milestone-slip',
      surface: 'work-item-hero',
      label: 'Milestone slip',
      value: milestoneSlipValue ?? '—',
      unit:
        typeof milestoneSlipValue === 'number'
          ? milestoneSlipValue === 1
            ? 'milestone'
            : 'milestones'
          : undefined,
      status: deriveKpiTone({
        value: typeof milestoneSlipValue === 'number' ? milestoneSlipValue : 0,
        target: 0,
        invert: true,
        atRisk: typeof milestoneSlipValue === 'number' && milestoneSlipValue > 0,
      }),
      footer:
        baselineVarianceValue != null
          ? `${baselineVarianceValue}d max baseline variance`
          : 'Milestones forecast past baseline',
    },
    {
      id: 'critical-path',
      surface: 'work-item-hero',
      label: 'Critical path',
      value: criticalPathValue ?? '—',
      unit:
        typeof criticalPathValue === 'number'
          ? criticalPathValue === 1
            ? 'item'
            : 'items'
          : undefined,
      status: typeof criticalPathValue === 'number' && criticalPathValue > 0 ? 'amber' : 'slate',
      footer:
        deliverySummaryData?.totalCriticalPathDays != null
          ? `${deliverySummaryData.totalCriticalPathDays}d critical span`
          : 'Items on the critical path',
    },
  ];

  return (
    <>
      <PageHeader
        eyebrow="OWHA Delivery"
        title={workItem.title || workItem.id}
        subtitle="Checklist, dependency, schedule, and execution posture for a governed delivery record."
      >
        <Breadcrumbs
          items={[{ label: 'Delivery workspace', to: deliveryLink }, { label: workItem.id }]}
        />
      </PageHeader>

      <PageActionStrip
        actions={
          <>
            <Link className="btn secondary sm" to={deliveryLink}>
              Open project delivery
            </Link>
            <Link className="btn ghost sm" to={roadmapRoute}>
              Open roadmap
            </Link>
            <Link className="btn ghost sm" to={{ search: logTimeSearch }}>
              Log time
            </Link>
            <button
              className="btn ghost sm"
              onClick={() => moveWorkItem('review')}
              disabled={update.running}
            >
              Move to review
            </button>
            <button
              className="btn success sm"
              onClick={() => moveWorkItem('done')}
              disabled={update.running}
            >
              Mark work item done
            </button>
          </>
        }
      />

      <section className="delivery-detail-shell delivery-redesign">
        <KpiHeroBand
          eyebrow="Execution posture"
          context={`Status · ${
            WORK_ITEM_LIFECYCLE_LABELS[currentStatus] ||
            STATUS_DISTRIBUTION_LABELS[currentStatus] ||
            currentStatus
          }`}
          items={heroItems}
          columns={4}
        />

        <article className="card delivery-lifecycle-card">
          <div className="row spread" style={{ alignItems: 'baseline', flexWrap: 'wrap', gap: 8 }}>
            <div className="eyebrow">Lifecycle</div>
            <span className={`chip ${statusTone(currentStatus)} sm`}>
              <span className="chip-dot" />
              {WORK_ITEM_LIFECYCLE_LABELS[currentStatus] ||
                STATUS_DISTRIBUTION_LABELS[currentStatus] ||
                currentStatus}
            </span>
          </div>
          <WorkflowPipeline steps={pipelineSteps} ariaLabel="Work item execution lifecycle" />
          {isBlocked ? (
            <p className="muted small delivery-lifecycle-note">
              This work item is impeded mid-flow. Clear blocking dependencies to resume progress
              toward review and closeout.
            </p>
          ) : null}
        </article>

        <div className="bento" style={{ gap: 16 }}>
          <article className="span-7 card delivery-detail-card">
            <div className="row" style={{ gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
              <span className="chip outline sm">{workItem.id}</span>
              <StatusChip status={workItem.status || 'open'} />
              <span className="chip outline sm">
                {workItem.type || workItem.itemType || 'story'}
              </span>
              <span className="chip outline sm">{workItem.priority || 'medium'} priority</span>
            </div>
            <p className="delivery-detail-copy">
              {workItem.description ||
                workItem.summary ||
                workItem.acceptanceSummary ||
                'No detailed narrative is available for this work item.'}
            </p>
            <dl className="inspector-dl">
              <div>
                <dt>Assigned</dt>
                <dd>{workItem.assignedToUserId || workItem.ownerUserId || '—'}</dd>
              </div>
              <div>
                <dt>Project</dt>
                <dd>{projectId || '—'}</dd>
              </div>
              <div>
                <dt>Sprint</dt>
                <dd>{workItem.sprintId || '—'}</dd>
              </div>
              <div>
                <dt>Completion</dt>
                <dd>{workItem.percentComplete ?? 0}%</dd>
              </div>
              <div>
                <dt>Source</dt>
                <dd className="mono">
                  {workItem.sourceSystem || workItem.dataTrust?.sourceSystem || 'PRIME'}
                </dd>
              </div>
              <div>
                <dt>Acceptance evidence</dt>
                <dd>{workItem.acceptanceSummary || 'No acceptance summary recorded.'}</dd>
              </div>
            </dl>
          </article>

          <article className="span-5 card delivery-detail-card">
            <div className="eyebrow">Schedule posture</div>
            <dl className="inspector-dl">
              <div>
                <dt>Start</dt>
                <dd>{workItem.startDate || 'Not set'}</dd>
              </div>
              <div>
                <dt>Due</dt>
                <dd>{workItem.dueDate || workItem.endDate || workItem.targetDate || 'Not set'}</dd>
              </div>
              <div>
                <dt>Baseline start</dt>
                <dd>{workItem.baselineStart || workItem.baselineStartDate || 'Not established'}</dd>
              </div>
              <div>
                <dt>Baseline end</dt>
                <dd>{workItem.baselineEnd || workItem.baselineEndDate || 'Not established'}</dd>
              </div>
              <div>
                <dt>Actual start</dt>
                <dd>{workItem.actualStart || workItem.actualStartDate || 'Not started'}</dd>
              </div>
              <div>
                <dt>Actual end</dt>
                <dd>{workItem.actualEnd || workItem.actualEndDate || 'In progress'}</dd>
              </div>
            </dl>
            {distributionTotal > 0 ? (
              <div className="delivery-distribution">
                <div className="eyebrow">Project work distribution</div>
                <StatusDonutMini
                  segments={distributionSegments}
                  size={104}
                  totalLabel="Work items"
                  centerValue={distributionTotal}
                  ariaLabel="Project work-item status distribution"
                />
              </div>
            ) : null}
          </article>
        </div>

        <section
          className={`delivery-inspector-section tone-section tone-${
            checklistPct == null ? 'slate' : checklistPct >= 100 ? 'sage' : 'blue'
          }`}
        >
          <div className="row spread">
            <div>
              <h4 className="delivery-inspector-title">Execution checklist</h4>
              <p className="muted small">
                Track entry/exit steps and evidence for this delivery record.
              </p>
            </div>
            <div className="delivery-checklist-progress">
              {checklistTotal > 0 ? (
                <>
                  <div className="delivery-checklist-progress__meta">
                    <span className="delivery-checklist-progress__pct">{checklistPct}%</span>
                    <span className="muted small">
                      {checklistDone}/{checklistTotal} complete
                    </span>
                  </div>
                  <MiniBars
                    values={[checklistDone, checklistRemaining]}
                    width={72}
                    height={28}
                    tone={checklistPct >= 100 ? 'sage' : 'blue'}
                    ariaLabel={`${checklistDone} of ${checklistTotal} checklist items complete`}
                  />
                </>
              ) : (
                <span className="chip outline sm">{checklistItems.length} items</span>
              )}
            </div>
          </div>
          {checklist.loading ? (
            <div className="muted small">Loading checklist…</div>
          ) : checklist.error ? (
            <ErrorState error={checklist.error} />
          ) : checklistItems.length === 0 ? (
            <BrandedEmptyState
              icon="✅"
              eyebrow="Execution checklist"
              title="No checklist items yet"
              description="Add entry/exit validation steps to evidence governed delivery for this work item."
              tone="slate"
            />
          ) : (
            <ul className="delivery-checklist-list">
              {checklistItems.map((item) => (
                <li key={item.id} className="delivery-checklist-row">
                  <div className="stack tight">
                    <strong>{item.label}</strong>
                    <span className="muted small">
                      {item.ownerUserId || 'Unassigned'}
                      {item.dueDate ? ` · due ${item.dueDate}` : ''}
                    </span>
                  </div>
                  <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
                    <StatusChip status={item.status || 'open'} />
                    <button
                      className="btn ghost sm"
                      onClick={() =>
                        updateChecklistItem(item, item.status === 'done' ? 'open' : 'done')
                      }
                      disabled={checklistMutation.running}
                    >
                      {item.status === 'done' ? 'Reopen' : 'Mark done'}
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
          <div className="delivery-inline-form">
            <label className="delivery-inline-field">
              <span>Checklist item label</span>
              <input
                aria-label="Checklist item label"
                value={newChecklistLabel}
                onChange={(event) => setNewChecklistLabel(event.target.value)}
                placeholder="Add a delivery validation step"
              />
            </label>
            <label className="delivery-inline-field">
              <span>Due date</span>
              <input
                aria-label="Due date"
                type="date"
                value={newChecklistDueDate}
                onChange={(event) => setNewChecklistDueDate(event.target.value)}
              />
            </label>
            <button
              className="btn secondary sm"
              onClick={addChecklistItem}
              disabled={checklistMutation.running || !newChecklistLabel.trim()}
            >
              Add checklist item
            </button>
          </div>
        </section>

        <section
          className={`delivery-inspector-section tone-section tone-${
            dependencyRows.some((d) => d.criticalPathFlag) ? 'amber' : 'slate'
          }`}
        >
          <div className="row spread">
            <div>
              <h4 className="delivery-inspector-title">Dependency links</h4>
              <p className="muted small">Manage predecessor ties that shape the critical path.</p>
            </div>
            <span className="chip outline sm">{dependencyRows.length} links</span>
          </div>
          {dependencyLinks.loading ? (
            <div className="muted small">Loading dependencies…</div>
          ) : dependencyLinks.error ? (
            <ErrorState error={dependencyLinks.error} />
          ) : dependencyRows.length === 0 ? (
            <BrandedEmptyState
              icon="🔗"
              eyebrow="Dependency links"
              title="No linked dependencies yet"
              description="Link predecessor work items to model the critical path and surface blocking risk."
              tone="slate"
            />
          ) : (
            <ul className="delivery-dependency-list">
              {dependencyRows.map((dependency) => (
                <li
                  key={dependency.id}
                  className={`delivery-dependency-row ${
                    dependency.criticalPathFlag ? 'sev-brick' : 'sev-slate'
                  }`}
                >
                  <div className="stack tight">
                    <strong>
                      {dependency.relationLabel}: {dependency.relatedId}
                    </strong>
                    <span>{dependency.relatedTitle}</span>
                    <span className="muted small">
                      {dependency.dependencyType?.replace(/_/g, ' ') || 'finish to start'}
                      {typeof dependency.lagDays === 'number'
                        ? ` · ${dependency.lagDays} day lag`
                        : ''}
                    </span>
                  </div>
                  <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
                    {dependency.criticalPathFlag ? (
                      <span className="chip red sm">Critical path</span>
                    ) : null}
                    <button
                      className="btn ghost sm"
                      onClick={() => removeDependencyLink(dependency)}
                      disabled={dependencyMutation.running}
                      aria-label={`Remove dependency ${dependency.id}`}
                    >
                      Remove
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
          <div className="delivery-inline-form">
            <label className="delivery-inline-field">
              <span>Predecessor work item</span>
              <select
                aria-label="Predecessor work item"
                value={newDependencyPredecessorId}
                onChange={(event) => setNewDependencyPredecessorId(event.target.value)}
              >
                <option value="">Select predecessor</option>
                {availablePredecessors.map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.id} · {item.title}
                  </option>
                ))}
              </select>
            </label>
            <label className="delivery-inline-field">
              <span>Lag days</span>
              <input
                type="number"
                min="0"
                value={newDependencyLagDays}
                onChange={(event) => setNewDependencyLagDays(event.target.value)}
              />
            </label>
            <button
              className="btn secondary sm"
              onClick={addDependencyLink}
              disabled={dependencyMutation.running || !newDependencyPredecessorId}
            >
              Link predecessor
            </button>
          </div>
        </section>
      </section>

      <TimeEntryDrawer
        open={timeDrawerOpen}
        onClose={closeTimeDrawer}
        targetEntityType="work_item"
        targetEntityId={workItem.id}
        targetTitle={workItem.title || workItem.id}
        projectId={projectId || null}
        workItemId={workItem.id}
        category="delivery"
      />
    </>
  );
}
